import type { AlertErrorHandlerFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import { type Job } from '@/features/jobs/Job'
import { type BusinessProfileStore } from '@/features/jobs/info-gathering/business-info/BusinessProfileStore'
import { type BusinessInfoViewModel } from '@/features/jobs/info-gathering/business-info/BusinessInfoViewModel'
import { type InfoGatheringStore } from '@/features/jobs/info-gathering/InfoGatheringStore'
import { BusinessProfileEditorViewModel } from '@/features/jobs/info-gathering/business-info/BusinessProfileEditorViewModel'

/**
 * View model for the dialog editing the business profile a job captured.
 */
export class EditBusinessProfileDialogViewModel {
  readonly modal = new ModalViewModel()

  readonly businessProfileEditorVm = new BusinessProfileEditorViewModel()

  constructor(
    private readonly _job: Job,
    private readonly _getBusinessInfoVm: () => BusinessInfoViewModel | null,
    private readonly _businessProfileStore: BusinessProfileStore,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Whether the business profile has been fetched, so the dialog can be
   * shown.
   */
  @computed
  get canShow() {
    return this._getBusinessInfoVm()?.isLoaded ?? true
  }

  /**
   * Shows the dialog with the current business profile.
   */
  @action.bound
  show() {
    this.businessProfileEditorVm.reset(this._getBusinessInfoVm()?.profile)
    this.modal.show()
  }

  /**
   * Saves the business profile and closes the dialog.
   */
  submit = task.resolved(async () => {
    if (!this.businessProfileEditorVm.validation.reset().validate().isValid) {
      return
    }

    const saved = await this._save()
      .then(() => true)
      .catch(
        this._createAlertErrorHandler('Saving the business profile failed'),
      )
      .catch(() => false)
    if (!saved) {
      return
    }

    this.modal.close()
  })

  private async _save() {
    const editor = this.businessProfileEditorVm
    const existingProfile = this._getBusinessInfoVm()?.profile
    const businessInfo = editor.businessInfoVm.toBusinessInfo()
    const contactInfo = editor.contactInfoVm.toContactInfo()

    const saved = existingProfile?.id
      ? await this._businessProfileStore.updateBusinessProfile({
          id: existingProfile.id,
          workspaceId: this._job.workspace.id,
          ownerUserId: this._job.championClient!.member.user.publicId,
          businessInfo,
          contactInfo,
        })
      : await this._businessProfileStore.createBusinessProfile({
          workspaceId: this._job.workspace.id,
          ownerUserId: this._job.championClient!.member.user.publicId,
          businessInfo,
          contactInfo,
        })

    await this._infoGatheringStore.provideSupportingItemBusinessInfo({
      jobId: this._job.id,
      itemId: this._getBusinessInfoVm()!.item.id,
      profileId: saved.id,
      profileVersion: saved.version,
    })
  }
}
